// apps/client/src/ui-kit/Layouts.tsx
//
// Layout components: JackpotHeader, BottomNav
// V2: Flat bottom nav with equal-size tabs, golden glow on active
//

import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';

// SVG icon imports for bottom nav
import icStore from '../assets/icons/store.svg';
import icGamepad from '../assets/icons/gamepad.svg';
import icHome from '../assets/icons/home.svg';
import icTrophy from '../assets/icons/thropy.svg';
import icUser from '../assets/icons/user.svg';

// --- JACKPOT HEADER ---
interface JackpotHeaderProps {
    amount: string;
    currency?: string;
}

export const JackpotHeader: React.FC<JackpotHeaderProps> = ({ amount, currency = '$' }) => {
    const { t } = useTranslation();
    return (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E1150] to-[#0F0A2E] p-8 border border-primary/30 shadow-glow-gold group max-w-sm mx-auto w-full">
            <div className="absolute -right-4 -top-4 text-9xl opacity-10 rotate-12 transition-transform group-hover:rotate-6 duration-700 select-none">🏆</div>
            <div className="relative z-10 flex justify-between items-center">
                <div>
                    <div className="inline-block px-2 py-0.5 bg-primary text-gold-text text-[9px] font-black rounded mb-3 uppercase tracking-wider">{t('game.liveJackpot')}</div>
                    <h4 className="text-4xl font-black text-white tracking-tight font-display">
                        {currency}{amount}
                    </h4>
                </div>
                <button className="px-6 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-pill text-xs font-black uppercase border border-white/20 transition-all">
                    {t('game.enter')}
                </button>
            </div>
            <div className="mt-6 w-full bg-black/40 h-2 rounded-full overflow-hidden">
                <div className="w-3/4 h-full bg-primary shadow-glow-gold"></div>
            </div>
        </div>
    );
};

// --- BOTTOM NAV (V2 — Flat, Equal Tabs, Golden Glow) ---

interface NavItem {
    id: string;
    icon: string; // SVG src path
    label: string;
}

interface BottomNavProps {
    activeTab: string;
    onTabChange: (id: string) => void;
    /** Tab IDs that should show a red notification dot */
    notificationDots?: string[];
}

export const BottomNav: React.FC<BottomNavProps> = ({
    activeTab,
    onTabChange,
    notificationDots = [],
}) => {
    const { t } = useTranslation();

    // UX order: Store | Daily | Home (center) | Sunday | Profile
    const items: NavItem[] = [
        { id: 'store', icon: icStore, label: t('nav.store') },
        { id: 'game', icon: icGamepad, label: t('nav.daily') },
        { id: 'home', icon: icHome, label: t('nav.home') },
        { id: 'sunday', icon: icTrophy, label: t('nav.sunday') },
        { id: 'profile', icon: icUser, label: t('nav.profile') },
    ];

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 glass-panel-deep border-t border-white/10" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}>
            <nav className="flex items-stretch justify-around max-w-lg mx-auto h-[60px]">
                {items.map((item) => {
                    const isActive = activeTab === item.id;
                    const hasDot = notificationDots.includes(item.id);

                    return (
                        <button
                            key={item.id}
                            onClick={() => onTabChange(item.id)}
                            className={cn(
                                'relative flex flex-col items-center justify-center flex-1 gap-1 transition-all duration-200 cursor-pointer',
                                isActive ? '' : 'opacity-50 hover:opacity-70'
                            )}
                        >
                            {/* Notification dot */}
                            {hasDot && (
                                <span className="absolute top-2 right-1/4 w-2 h-2 bg-red-500 rounded-full animate-pulse z-10" />
                            )}

                            {/* Icon with golden glow when active */}
                            <div className={cn(
                                'relative flex items-center justify-center w-7 h-7 transition-all duration-300',
                                isActive ? 'drop-shadow-[0_0_8px_rgba(251,191,36,0.7)]' : ''
                            )}>
                                <img
                                    src={item.icon}
                                    alt=""
                                    className={cn(
                                        'w-6 h-6 transition-all duration-300',
                                        isActive ? 'brightness-0 invert sepia saturate-[10] hue-rotate-[15deg]' : 'brightness-0 invert opacity-70'
                                    )}
                                />
                            </div>

                            {/* Label */}
                            <span className={cn(
                                'text-[10px] font-bold uppercase tracking-wider transition-colors duration-200',
                                isActive ? 'text-[#FBBF24]' : 'text-white/50'
                            )}>
                                {item.label}
                            </span>

                            {/* Active golden bar indicator */}
                            {isActive && (
                                <motion.div
                                    layoutId="nav-indicator"
                                    className="absolute -top-[1px] left-2 right-2 h-[2px] bg-gradient-to-r from-transparent via-[#FBBF24] to-transparent rounded-full"
                                    transition={{ type: 'spring', bounce: 0.2 }}
                                />
                            )}
                        </button>
                    );
                })}
            </nav>
        </div>
    );
};
